import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useMemo } from "react";
import _ from "lodash";
import {
    addMarkerReviewFilter,
    removeMarkerReviewFilter,
    updateThresholdFilter,
    addMarkerConditionFilter,
    removeMarkerConditionFilter,
    setTroughingFilter,
    setMarkerConditionFilter,
    setLowBallastConfidenceFilter,
    setVegetationEncroachmentFilters,
    setThermalHotspotFilters,
    addObservationConditionFilter,
    removeObservationConditionFilter,
    setObservationConditionFilters,
} from "../redux/actions/index";
import { Button, Checkbox, InputNumber, Modal, Slider, Select } from "antd";
import { degreesToUserPreference, farenheitToCelsius } from "./util/TemperatureUtils";

const emptyArray = [];
const emptyObject = {};
const filtersArraySelector = (state) => state.markerReviewFilters || emptyArray;
const filtersConditionObjectSelector = (state) => state.markerConditionFilter || emptyObject;
const detectionTypeSelector = (state) => state.detectionTypes;
const thresholdFilterSelector = (state) => state.markerThresholdFilters;
const conditionTypesSelector = (state) => {
    const currentSession = state.sessions[state.playlist.data.routeID];
    return _.get(state.markers.conditionTypes, [currentSession?.map_system], emptyArray);
};
const observationFilterSelector = (state) => state.observationFilters;
const selectedMarkerSelector = (state) => state.markers.selectedMarker;
const sessionObservationsSelector = (state) => state.sessionObservations;
const tempUnitPreferenceSelector = (state) => state.userDetails.userConfig.temperature_units || "celsius";
const lowBallastConfidenceFilterSelector = (state) => state.observationFilters.lowBallastConfidenceFilter;
const sessionDataSelector = (state) => _.get(state.sessions, [state.playlist.data.routeID], null);

const filterKeyMap = {
    score: "selected_score",
    ambient: "selected_ambient_score",
};

const filterLabelMap = {
    pc: "Percent confidence",
    degrees: "Min temp",
};

const MarkerThresholdFiltersModal = ({ markerThresholdFilterOpen, setMarkerThresholdFilterOpen, filterWithSelectedMarker = true }) => {
    let dispatch = useDispatch();

    const filtersArray = useSelector(filtersArraySelector);
    const filtersConditionObject = useSelector(filtersConditionObjectSelector);
    const detectionTypes = useSelector(detectionTypeSelector);
    const thresholdFilters = useSelector(thresholdFilterSelector);
    const conditionTypes = useSelector(conditionTypesSelector);
    const currentRouteID = useSelector((state) => state.playlist.data.routeID);
    const defaultMarkers = useSelector((state) => state.defaultMarkersThresholds[currentRouteID] || emptyObject);
    const marker = useSelector((state) => state.markers);
    const observationFilters = useSelector(observationFilterSelector);
    const selectedMarker = useSelector(selectedMarkerSelector);
    const sessionObservations = useSelector(sessionObservationsSelector);
    const tempUnitPreference = useSelector(tempUnitPreferenceSelector);
    const lowBallastConfidenceFilter = useSelector(lowBallastConfidenceFilterSelector);
    const sessionData = useSelector(sessionDataSelector);

    const { Option } = Select;

    const markerName = useMemo(() => {
        if (!filterWithSelectedMarker) {
            return null;
        }
        return _.get(selectedMarker, "class", _.get(selectedMarker, "name", null));
    }, [filterWithSelectedMarker, selectedMarker]);

    const markersAndObservations = useMemo(() => {
        let combinedUniqueMarkers = [];
        if (sessionObservations && marker.perSession[currentRouteID]) {
            combinedUniqueMarkers = _.uniq([
                ...marker.perSession[currentRouteID].map((item) => item.name.toLowerCase()),
                ...sessionObservations.map((item) => item.class.toLowerCase()),
            ]);
        }
        return combinedUniqueMarkers;
    }, [sessionObservations, marker, currentRouteID]);

    const filterableTypes = useMemo(() => {
        const result = [];

        detectionTypes.forEach((item) => {
            if (!markersAndObservations.includes(item.type.toLowerCase())) return;
            if (item.min_score != null || item.max_score != null) {
                const label = filterLabelMap[item.score_units] || "Min score";
                result.push({ detectionType: item.type, sliderLabel: label, detection: item, sliderType: "score" });
            }

            if (item.min_ambient_score != null || item.max_ambient_score != null) {
                result.push({ detectionType: item.type, sliderLabel: "Min average surface temp", detection: item, sliderType: "ambient" });
            }
        });

        return result;
    }, [detectionTypes, markersAndObservations]);

    const selectMarkerType = (markerType) => {
        if (_.includes(filtersArray, markerType)) {
            dispatch(removeMarkerReviewFilter(markerType));
        } else {
            dispatch(addMarkerReviewFilter(markerType));
        }
    };

    const onThresholdFilterChange = useCallback(
        (key, value, isObservation, thresholdType) => {
            if (isObservation) {
                if (key === "Thermal Hotspot") {
                    const filterKey = filterKeyMap[thresholdType] || "selected_score";
                    dispatch(setThermalHotspotFilters({ [filterKey]: value }));
                } else {
                    dispatch(setThermalHotspotFilters({ selected_score: value }));
                }
                return;
            }
            dispatch(updateThresholdFilter(key, value));
        },
        [dispatch],
    );

    const changeAuto = useCallback(
        (isAuto, type, isObservation, thresholdType) => {
            if (isObservation) {
                const filterValue = isAuto ? undefined : _.get(type, ["default_score"], 50);
                onThresholdFilterChange(type.type, filterValue, true, thresholdType);
                return;
            }

            const defValue = _.get(defaultMarkers, [type.type], type.default_score);
            const filterValue = isAuto ? undefined : _.get(thresholdFilters, [type.type], defValue);
            onThresholdFilterChange(type.type, filterValue, false);
        },
        [onThresholdFilterChange, defaultMarkers, thresholdFilters],
    );

    const toggleMarkerCondition = useCallback(
        (checked, conditionType, conditionID) => {
            if (checked) {
                dispatch(addMarkerConditionFilter(conditionType, conditionID));
                dispatch(addObservationConditionFilter(conditionType, conditionID));
            } else {
                dispatch(removeMarkerConditionFilter(conditionType, conditionID));
                dispatch(removeObservationConditionFilter(conditionType, conditionID));
            }
        },
        [dispatch],
    );

    const toggleAllConditions = useCallback(
        (checked, conditionType, conditions) => {
            if (checked) {
                const newFilters = _.cloneDeep(filtersConditionObject);
                newFilters[conditionType] = [null, ..._.map(conditions, "id")];
                dispatch(setMarkerConditionFilter(newFilters));
                dispatch(setObservationConditionFilters(newFilters));
            } else {
                const newFilters = _.cloneDeep(filtersConditionObject);
                newFilters[conditionType] = [];
                dispatch(setMarkerConditionFilter(newFilters));
                dispatch(setObservationConditionFilters(newFilters));
            }
        },
        [dispatch, filtersConditionObject],
    );

    const getObservationFilterValue = useCallback(
        (key, defaultValue, thresholdType) => {
            const filterKey = filterKeyMap[thresholdType] || "selected_score";
            if (observationFilters[key] && observationFilters[key][filterKey] !== undefined) {
                return observationFilters[key][filterKey];
            }
            return defaultValue;
        },
        [observationFilters],
    );

    const sliderFilters = useMemo(() => {
        return filterableTypes
            .map((filterItem, index) => {
                if ((markerName && filterItem.detection.type.toLowerCase() === markerName.toLowerCase()) || !markerName) {
                    const detectionType = _.get(filterItem.detection, "detection_type", undefined);
                    let isObservation = detectionType && detectionType.includes("observation");

                    if (sessionData && sessionData.first_seen < 1761649200 && filterItem.detection.type === "Thermal Hotspot") {
                        isObservation = false;
                    }

                    const defValue = _.get(defaultMarkers, [filterItem.detection.type], filterItem.default_score);
                    const markerFilterValue = _.get(thresholdFilters, [filterItem.detection.type], defValue);
                    const observationFilterValue = getObservationFilterValue(
                        filterItem.detection.type,
                        filterItem.detection.default_score,
                        filterItem.sliderType,
                    );
                    const filterValue = isObservation ? observationFilterValue : markerFilterValue;

                    const filterKey = filterKeyMap[filterItem.sliderType] || "selected_score";
                    let isAuto = isObservation
                        ? _.get(observationFilters, [filterItem.detection.type, filterKey], undefined) === undefined
                        : thresholdFilters[filterItem.detection.type] === undefined;

                    let minScore = filterItem.detection.min_score;
                    let maxScore = filterItem.detection.max_score;
                    let scoreUnits = filterItem.detection.score_units;
                    let valueConverter = (val) => val;
                    let reverseConverter = (val) => val;

                    if (scoreUnits === "degrees") {
                        minScore = degreesToUserPreference(minScore, tempUnitPreference);
                        maxScore = degreesToUserPreference(maxScore, tempUnitPreference);
                        scoreUnits = tempUnitPreference;
                        valueConverter = (value) => degreesToUserPreference(value, tempUnitPreference);
                        if (tempUnitPreference === "fahrenheit") {
                            reverseConverter = (value) => {
                                const converted = degreesToUserPreference(value, tempUnitPreference, true);
                                return converted;
                            };
                            reverseConverter = (value) => farenheitToCelsius(value);
                        }
                    }

                    return (
                        <div
                            className="SliderFilterRow"
                            key={"MarkerConditionTypeKey" + filterItem.detection.type + index}>
                            <span className="Text">
                                {filterItem.detection.type} {filterItem.sliderLabel ? `- ${filterItem.sliderLabel}` : null}
                                <span>({isObservation ? "observation" : "marker"})</span>
                            </span>
                            <div className="SliderFilterElements">
                                <div className="SliderContainer">
                                    <Slider
                                        onChange={(value) =>
                                            onThresholdFilterChange(filterItem.detection.type, reverseConverter(value), isObservation, filterItem.sliderType)
                                        }
                                        disabled={isAuto}
                                        min={minScore}
                                        max={maxScore}
                                        value={valueConverter(filterValue)}
                                        marks={{
                                            [minScore]: `${minScore}`,
                                            [maxScore]: `${maxScore}`,
                                        }}
                                        tipFormatter={(value) => `${value} ${scoreUnits}`}
                                        className="Slider"
                                    />
                                    <p className="Units">{scoreUnits}</p>
                                </div>
                                <InputNumber
                                    min={minScore}
                                    max={maxScore}
                                    value={valueConverter(filterValue)}
                                    disabled={isAuto}
                                    style={{ marginLeft: 16, marginRight: 16 }}
                                    onChange={(value) =>
                                        onThresholdFilterChange(filterItem.detection.type, reverseConverter(value), isObservation, filterItem.sliderType)
                                    }
                                    className="Input"
                                    size="small"
                                />
                                <Checkbox
                                    checked={isAuto}
                                    onChange={(e) => changeAuto(e.target.checked, filterItem.detection, isObservation, filterItem.sliderType)}>
                                    Auto
                                </Checkbox>
                            </div>
                        </div>
                    );
                }
                return null;
            })
            .filter((item) => item !== null);
    }, [
        filterableTypes,
        thresholdFilters,
        onThresholdFilterChange,
        defaultMarkers,
        changeAuto,
        markerName,
        tempUnitPreference,
        observationFilters,
        getObservationFilterValue,
    ]);

    const conditionFilters = useMemo(() => {
        let filteredConditionTypes = markerName
            ? conditionTypes.filter((item) => item.marker_type.toLowerCase() === markerName.toLowerCase())
            : conditionTypes.filter((item) => markersAndObservations.includes(item.marker_type.toLowerCase()));

        const groupedConditions = _.groupBy(filteredConditionTypes, "marker_type");
        let markerTypesPerSession = emptyObject;

        if (marker && currentRouteID) {
            markerTypesPerSession = _.groupBy(marker.perSession[currentRouteID], "name");
        }

        const isChecked = (name, condition) => {
            if (filtersConditionObject[name]) {
                return filtersConditionObject[name].includes(condition.id);
            } else {
                return false;
            }
        };

        return _.map(groupedConditions, (conditions, name, index) => {
            if (markerTypesPerSession[name] || markersAndObservations.includes(name.toLowerCase())) {
                const allChecked = filtersConditionObject[name]?.length === conditions.length + 1;
                return (
                    <div
                        className="markerCondition"
                        key={"MarkerConditionKey" + name + index}>
                        <span className="markerConditionLabel">{name}</span>
                        <div className="markerConditionList">
                            <Checkbox
                                className="markerReviewFilter-Checkbox"
                                key={null}
                                checked={isChecked(name, { id: null })}
                                onChange={(e) => toggleMarkerCondition(e.target.checked, name, null)}>
                                None
                            </Checkbox>
                            {_.map(conditions, (condition) => {
                                return (
                                    <Checkbox
                                        className="markerReviewFilter-Checkbox"
                                        key={condition.id}
                                        checked={isChecked(name, condition)}
                                        onChange={(e) => toggleMarkerCondition(e.target.checked, name, condition.id)}>
                                        {condition.condition_name}
                                    </Checkbox>
                                );
                            })}

                            <Checkbox
                                className="markerReviewFilter-Checkbox"
                                checked={allChecked}
                                onChange={(e) => toggleAllConditions(e.target.checked, name, conditions)}>
                                All
                            </Checkbox>
                        </div>
                    </div>
                );
            }
        });
    }, [markerName, conditionTypes, marker, currentRouteID, markersAndObservations, filtersConditionObject, toggleMarkerCondition, toggleAllConditions]);

    const setTroughingFilters = (e, filterType) => {
        if (filterType === "condition") {
            dispatch(setTroughingFilter(e, observationFilters.troughingType));
        }
        if (filterType === "type") {
            dispatch(setTroughingFilter(observationFilters.condition, e));
        }
    };

    const displayTroughingFilters = useMemo(() => {
        if (!_.isEmpty(selectedMarker)) {
            return selectedMarker.class === "Troughing";
        } else {
            return markersAndObservations.includes("troughing");
        }
    }, [selectedMarker, markersAndObservations]);

    const renderLowBallastConfidenceFilter = useMemo(() => {
        if ((markerName === "Low Ballast" || markerName === "low_ballast" || markerName === null) && _.includes(markersAndObservations, "low_ballast")) {
            return (
                <div className="LowBallast">
                    <h3 className="LowBallastTitle">Low Ballast</h3>
                    <div className="LowBallastContainer">
                        <span className="LowBallastLabel">Confidence</span>
                        <Slider
                            className="LowBallastConfidenceFilter"
                            range={true}
                            min={0}
                            max={1}
                            marks={{
                                0: `0`,
                                1: `1`,
                            }}
                            step={0.01}
                            value={lowBallastConfidenceFilter}
                            onChange={(e) => dispatch(setLowBallastConfidenceFilter(e))}
                        />
                    </div>
                </div>
            );
        }

        return null;
    }, [markerName, dispatch, lowBallastConfidenceFilter, markersAndObservations]);

    const renderVegetationEncroachmentFilter = useMemo(() => {
        if (
            (markerName === "Vegetation Encroachment" || markerName === "vegetation_encroachment" || markerName === null) &&
            _.includes(markersAndObservations, "vegetation_encroachment")
        ) {
            return (
                <div className="VegetationEncroachment">
                    <h3 className="VegetationEncroachmentTitle">Vegetation Encroachment</h3>
                    <div className="VegetationEncroachmentContainer">
                        <h4>Percent range</h4>
                        <Slider
                            className="VegetationEncroachmentPercentRange"
                            range={true}
                            min={0}
                            max={100}
                            defaultValue={[0, 100]}
                            marks={{
                                0: `0%`,
                                100: `100%`,
                            }}
                            step={1}
                            onAfterChange={(e) => {
                                dispatch(setVegetationEncroachmentFilters({ ...observationFilters.vegetationFilters, percentageRange: e }));
                            }}
                        />
                    </div>
                </div>
            );
        }

        return null;
    }, [markerName, markersAndObservations, observationFilters.vegetationFilters, dispatch]);

    return (
        <Modal
            className="MarkerThresholdModal"
            title="Marker Filters"
            visible={markerThresholdFilterOpen}
            onOk={() => {
                setMarkerThresholdFilterOpen(false);
            }}
            onCancel={() => {
                setMarkerThresholdFilterOpen(false);
            }}
            footer={[
                <Button
                    type="primary"
                    onClick={() => {
                        setMarkerThresholdFilterOpen(false);
                    }}>
                    Close
                </Button>,
            ]}>
            <div className="MarkerThresholdPopup">
                <h3>Review Status Filters</h3>
                <div className="markerReviewFilter">
                    <Checkbox
                        className="markerReviewFilter-Checkbox"
                        key={1}
                        checked={_.includes(filtersArray, 1)}
                        onChange={() => {
                            selectMarkerType(1);
                        }}>
                        Verified
                    </Checkbox>
                    <Checkbox
                        className="markerReviewFilter-Checkbox"
                        key={2}
                        checked={_.includes(filtersArray, 2)}
                        onChange={() => {
                            selectMarkerType(2);
                        }}>
                        Verified & Hidden
                    </Checkbox>
                    <Checkbox
                        className="markerReviewFilter-Checkbox"
                        key={0}
                        checked={_.includes(filtersArray, 0)}
                        onChange={() => {
                            selectMarkerType(0);
                        }}>
                        Needs Review
                    </Checkbox>
                    <Checkbox
                        className="markerReviewFilter-Checkbox"
                        key={3}
                        checked={_.includes(filtersArray, 3)}
                        onChange={() => {
                            selectMarkerType(3);
                        }}>
                        Rejected
                    </Checkbox>
                </div>
                {!!sliderFilters.length && (
                    <>
                        <h3>Score Filters</h3>
                        <div className="MarkerThresholdFilter">{sliderFilters}</div>
                    </>
                )}

                {renderLowBallastConfidenceFilter}
                {renderVegetationEncroachmentFilter}

                {(!!conditionFilters[0] || displayTroughingFilters) && (
                    <div className="markerReviewCondition">
                        <h3>Conditions Filters</h3>
                        {conditionFilters}
                    </div>
                )}

                {displayTroughingFilters && (
                    <>
                        <h4 className="TroughingHeading">Troughing</h4>
                        <div className="TroughingFilters">
                            <Select
                                className="TroughingFilterSelect"
                                mode="multiple"
                                placeholder="Troughing Conditions"
                                onChange={(e) => setTroughingFilters(e, "condition")}
                                showArrow
                                maxTagTextLength={7}
                                maxTagCount={1}
                                value={observationFilters.condition}
                                allowClear>
                                <Option key={"defect"}>Defect</Option>
                                <Option key={"obscured"}>Obscured</Option>
                                <Option key={"cables"}>Cables outside of troughing</Option>
                                <Option key={"good"}>Good</Option>
                                <Option key={"invalid"}>Invalid</Option>
                                <Option key={"no_result"}>No result</Option>
                            </Select>
                            <Select
                                className="TroughingFilterSelect"
                                mode="multiple"
                                placeholder="Troughing Types"
                                onChange={(e) => setTroughingFilters(e, "type")}
                                showArrow
                                maxTagTextLength={7}
                                maxTagCount={1}
                                value={observationFilters.troughingType}
                                allowClear>
                                <Option key={"concrete"}>Concrete</Option>
                                <Option key={"composite"}>Composite</Option>
                                <Option key={"elevated"}>Elevated</Option>
                                <Option key={"uncertain"}>Uncertain</Option>
                                <Option key={"not troughing"}>Not troughing</Option>
                            </Select>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default MarkerThresholdFiltersModal;
